import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { toast } from '@/utils/toast.utils';
import type { HandlerOptions } from '@/interfaces/common.interface';
import { cleeService } from '../services/clee.service';
import { queryClient } from '@/plugins/vue-query';
import { useCurrentProject } from './useCurrentProject';
import { useTranslations } from '@/composables/useTranslations';

export const useDomainLookup = (domain: MaybeRefOrGetter<string>) => {
  const domainValue = computed(() => toValue(domain));
  const { currentProject } = useCurrentProject();
  const domainQueryIsEnabled = computed(() => !!domainValue.value);
  const { t } = useTranslations();

  const { data: response, isLoading } = useQuery({
    queryKey: ['domain-lookup', domainValue],
    queryFn: () => cleeService.lookupDomain(domainValue.value),
    enabled: domainQueryIsEnabled,
  });

  async function registerDomain(domain: string, options: HandlerOptions = { silent: false }) {
    if (!currentProject.value?.id) return;
    const loadingToast = !options.silent
      ? toast.loading({ message: t('notifications.register_domain_loading') })
      : null;
    try {
      const response = await cleeService.registerDomainByProjectId(
        domain,
        currentProject.value?.id,
      );
      loadingToast?.resolve({ message: t('notifications.register_domain_success') });
      await refetch();

      return response.data;
    } catch (error) {
      loadingToast?.reject({ message: t('notifications.register_domain_error') });
    }
  }

  async function refetch() {
    await queryClient.invalidateQueries({ queryKey: ['account-domains'] });
    await queryClient.invalidateQueries({ queryKey: ['domain-lookup'] });
    await queryClient.invalidateQueries({ queryKey: ['projects'] });
  }

  return {
    //data
    domain: computed(() => response.value?.data.data),
    response,
    isLoading,

    //methods
    registerDomain,
  };
};
